<template>
    <div>
        <b-card :header="uLang('app.entity_details', { entity: lang('app.organisation') })" header-tag="h4" class="bg-default-card" v-if="organisation">
            <div class="row">
                <div class="col-md-12">
                    <div class="inner">
                        <div class="mb-3">
                            <router-link class="btn btn-brand" :to="{ name: 'admin.organisations.edit', params: { id: this.id } }">{{ uLang('app.edit_type', { type: lang('app.organisation') }) }}</router-link>

                            <client-candidates-export-button
                                v-if="organisation.type.system_name === 'client' && $hasType('id_employee')"
                                class="float-right"
                                route-name="organisations.client-candidates.export"
                                :route-params="{ organisation: id }"
                            />
                        </div>

                        <table class="table">
                            <tr>
                                <td width="200">{{ uLang('app.name') }}:</td>
                                <td>{{ organisation.name }}</td>
                            </tr>
                            <tr>
                                <td width="200">{{ uLang('app.type') }}:</td>
                                <td>{{ organisation.type.name }}</td>
                            </tr>
                            <tr>
                                <td>{{ uLang('app.active') }}:</td>
                                <td><b-badge pill variant="primary">{{ uLang('app.' + (organisation.active ? 'yes' : 'no')) }}</b-badge></td>
                            </tr>
                            <tr v-if="organisation.website">
                                <td>{{ uLang('app.website') }}:</td>
                                <td><a :href="organisation.website" target="_blank">{{ organisation.website }}</a></td>
                            </tr>
                            <tr v-if="organisation.full_phone_number">
                                <td>{{ uLang('app.phone_number') }}:</td>
                                <td><tel :number="organisation.full_phone_number"/></td>
                            </tr>
                            <tr v-if="organisation.account_number">
                                <td>{{ uLang('app.account_number') }}:</td>
                                <td>{{ organisation.account_number }}</td>
                            </tr>
                            <tr v-if="organisation.coc_number">
                                <td>{{ uLang('app.coc_number') }}:</td>
                                <td>{{ organisation.coc_number }}</td>
                            </tr>
                            <tr v-if="organisation.vat_number">
                                <td>{{ uLang('app.vat_number') }}:</td>
                                <td>{{ organisation.vat_number }}</td>
                            </tr>
                            <tr v-if="organisation.note">
                                <td>{{ uLang('app.note') }}:</td>
                                <td>{{ organisation.note }}</td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>

            <div v-if="organisation.type.system_name !== 'id_employee'">
                <h4>{{ uLang('app.invoice_info') }}</h4>

                <table class="table">
                    <tr>
                        <td width="200">{{ uLang('app.email') }}:</td>
                        <td>{{ $getIfExists(organisation, ['email']) }}</td>
                    </tr>
                    <tr>
                        <td width="200">{{ uLang('app.salutation') }}:</td>
                        <td>{{ $getIfExists(organisation, ['salutation']) }}</td>
                    </tr>
                    <tr>
                        <td width="200">{{ uLang('app.country') }}:</td>
                        <td>{{ $getIfExists(organisation, ['country']) }}</td>
                    </tr>
                    <tr>
                        <td width="200">{{ uLang('app.zipcode') }}:</td>
                        <td>{{ $getIfExists(organisation, ['zipcode']) }}</td>
                    </tr>
                    <tr>
                        <td width="200">{{ uLang('app.city') }}:</td>
                        <td>{{ $getIfExists(organisation, ['city']) }}</td>
                    </tr>
                    <tr>
                        <td width="200">{{ uLang('app.street') }}:</td>
                        <td>{{ $getIfExists(organisation, ['street']) }}</td>
                    </tr>
                    <tr>
                        <td width="200">{{ uLang('app.house_number') }}:</td>
                        <td>{{ $getIfExists(organisation, ['house_number']) }}</td>
                    </tr>
                </table>
            </div>

            <div class="row">
                <div class="col-md-12 tabs-show-area">
                    <b-card no-body class="mt-3">
                        <b-tabs card v-model="organisationShowTabState">
                            <b-tab :title="uLang('app.relations')">
                                <div class="col-md-12">
                                    <relations-tab
                                        ref="relationsTab"
                                        :apiUrl="route('admin.organisations.relationTable', {
                                            organisation: organisation.id
                                        })"
                                        :entityTableUrl="'/api/admin/organisations/locationTable/' + id"
                                        :organisation-id="id"
                                    />
                                </div>
                            </b-tab>
                            <b-tab :title="uLang('app.locations')" active>
                                <div class="col-md-12">
                                    <locations-tab resource="organisations" :resource-id="id"/>
                               </div>
                            </b-tab>
                            <b-tab :title="uLang('app.documents')">
                                <documents-tab
                                    entityType="organisations"
                                    :entityId="organisation.id"
                                    :type-id="organisation.type_id"
                                />
                            </b-tab>
                            <b-tab :title="uLang('app.log')"
                                v-if="$can('view-intern-logs')
                                    || $can('view-report-logs')
                                    || $can('view-candidate-logs')"
                            >
                                <div class="col-md-12">
                                    <logs-tab
                                        entityType="organisations"
                                        :entityId="organisation.id"
                                    />
                                </div>
                            </b-tab>
                        </b-tabs>
                    </b-card>
                </div>
            </div>
        </b-card>

    </div>
</template>

<script>
    import { LocationsTab, DocumentsTab, RelationsTab, LogsTab } from '~/components/tabs';
    import AddressesDisplay from '~/components/addresses/display';
    import ClientCandidatesExportButton from '~/components/buttons/client-candidates-export-button';

    export default {
        components: {
            LogsTab,
            LocationsTab,
            RelationsTab,
            DocumentsTab,
            AddressesDisplay,
            ClientCandidatesExportButton,
        },

        name: 'show',

        props: {
            id: {
                required: true,
            },
        },

        data: function () {
            return {
                organisation: null,
            }
        },

        created() {
            this.retrieveOrganisation();
        },

        methods: {
            async retrieveOrganisation(){
                const { data } = await window.axios.get(route('organisations.show', { organisation: this.id }));
                this.organisation = data;
            },
        },
        computed: {
            organisationShowTabState: {
                get () { return this.$store.getters.tabState('organisation-show') },
                set (v) { this.$store.commit('SET_TAB_STATE', { name: 'organisation-show', value: v }) }
            }
        },
    }
</script>

<style lang="css" scoped>
    .detail-buttons {
        margin-bottom: 15px;
        float: right;
    }
    .add_address {
        margin-top: 15px;
        margin-bottom: 15px;
    }
    .action_icon {
        cursor: pointer;
        margin-right: 5px;
    }
    .tabs, .addresses {
        margin-top: 10px;
    }
    .address_block a{
        margin-right: 10px;
    }
</style>
